import React, { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Headphones, Send, Loader2, MessageSquare, LifeBuoy, Phone } from "lucide-react";

const SUGGESTIONS = [
    "How do I install my eSIM?",
    "How does porting my number work?",
    "Why was my service suspended?",
    "What's the senior discount?",
];

export default function Support() {
    const [sessionId] = useState(() => `sup_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
    const [messages, setMessages] = useState([{ role: "assistant", content: "Hi! I'm Flex, your FlexLine Mobile assistant. Ask me about plans, activation, porting or billing." }]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [ticket, setTicket] = useState({ subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const endRef = useRef(null);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || sending) return;
        setMessages((m) => [...m, { role: "user", content: msg }]);
        setInput("");
        setSending(true);
        try {
            const { data } = await api.post("/support/chat", { session_id: sessionId, message: msg });
            setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
        } catch (e) {
            setMessages((m) => [...m, { role: "assistant", content: "Sorry — I hit an error. Please try again or submit a ticket below and a human will help." }]);
        }
        setSending(false);
    };

    const submitTicket = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/support/ticket", ticket);
            toast.success("Ticket submitted — we'll email you shortly.");
            setTicket({ subject: "", message: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit ticket");
        }
        setSubmitting(false);
    };

    return (
        <div className="space-y-8" data-testid="support-page">
            <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-deep mb-2 flex items-center gap-1.5"><Headphones className="w-3.5 h-3.5" /> Support</p>
                <h1 className="font-display text-4xl font-light tracking-tight">How can we <span className="font-serif-italic">help?</span></h1>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* AI assistant */}
                <div className="lg:col-span-2 border border-[#DDD6FE] rounded-sm bg-paper flex flex-col" data-testid="support-chat" style={{ minHeight: 420 }}>
                    <div className="px-5 py-3 border-b border-[#DDD6FE] flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-emerald-deep" />
                        <span className="font-medium text-sm">Flex — AI assistant</span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: 380 }}>
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div data-testid={`chat-msg-${m.role}`} className={`max-w-[80%] rounded-sm px-3 py-2 text-sm ${m.role === "user" ? "bg-[#4C1D95] text-white" : "bg-cream text-espresso"}`}>
                                    {m.content}
                                </div>
                            </div>
                        ))}
                        {sending && <div className="flex justify-start"><div className="bg-cream rounded-sm px-3 py-2"><Loader2 className="w-4 h-4 animate-spin text-zinc-400" /></div></div>}
                        <div ref={endRef} />
                    </div>
                    <div className="px-4 pb-2 flex flex-wrap gap-2">
                        {SUGGESTIONS.map((s) => (
                            <button key={s} onClick={() => send(s)} disabled={sending} data-testid="chat-suggestion"
                                className="text-xs border border-[#DDD6FE] rounded-full px-3 py-1 hover:bg-cream">{s}</button>
                        ))}
                    </div>
                    <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[#DDD6FE] flex gap-2">
                        <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="chat-input"
                            placeholder="Ask Flex anything…" className="flex-1 border border-[#DDD6FE] rounded-sm h-10 px-3 text-sm" />
                        <Button type="submit" disabled={sending || !input.trim()} data-testid="chat-send-btn" className="bg-emerald-deep text-white rounded-sm h-10 px-4">
                            <Send className="w-4 h-4" />
                        </Button>
                    </form>
                </div>

                {/* Contact + ticket */}
                <div className="space-y-4">
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid="support-contact">
                        <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Contact us</h3>
                        <a href="tel:+18777380943" className="flex items-center gap-2 text-sm mb-2" data-testid="support-phone"><Phone className="w-4 h-4 text-emerald-deep" /> (877) 738-0943</a>
                        <a href="mailto:info@flexlines.store" className="flex items-center gap-2 text-sm" data-testid="support-email"><LifeBuoy className="w-4 h-4 text-emerald-deep" /> info@flexlines.store</a>
                    </div>
                    <form onSubmit={submitTicket} className="border border-[#DDD6FE] rounded-sm bg-paper p-5 space-y-3" data-testid="support-ticket-form">
                        <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Submit a ticket</h3>
                        <input required value={ticket.subject} onChange={(e) => setTicket((t) => ({ ...t, subject: e.target.value }))}
                            data-testid="ticket-subject" placeholder="Subject" className="w-full border border-[#DDD6FE] rounded-sm h-10 px-3 text-sm" />
                        <textarea required value={ticket.message} onChange={(e) => setTicket((t) => ({ ...t, message: e.target.value }))}
                            data-testid="ticket-message" placeholder="Describe your issue…" rows={4} className="w-full border border-[#DDD6FE] rounded-sm p-3 text-sm" />
                        <Button type="submit" disabled={submitting} data-testid="ticket-submit-btn" className="w-full bg-espresso text-[#FAF5FF] rounded-sm h-10">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit ticket"}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
